import { useLayoutEffect, type RefObject } from "react";
import { isPrerender } from "../utils/isPrerender";

/**
 * Entrance and scroll motion for the /career and /love-astrology landings.
 *
 * GSAP is loaded with a dynamic import so it stays out of the main bundle:
 * only these two ad landings pay for it, not every route.
 *
 * TWO THINGS THIS HOOK MUST NEVER DO.
 *
 * 1. Hide content during the prerender crawl. Both routes are in
 *    package.json reactSnap.include, so whatever the DOM looks like at
 *    snapshot time becomes the static HTML that crawlers and first-time
 *    visitors are served. An opacity:0 baked into that file is an invisible
 *    page. Hence the isPrerender() bail before anything is touched.
 *
 * 2. Leave content hidden if GSAP never arrives. The pre-animation class is
 *    applied synchronously (useLayoutEffect, before paint, so there is no
 *    flash of unstyled content), but the import that clears it is async and
 *    can fail: offline, a chunk 404 after a deploy, a blocked CDN. The
 *    failsafe timeout removes the class regardless, so the worst case is an
 *    un-animated page rather than a blank one.
 */
export function useCareerMotion(ref: RefObject<HTMLElement>) {
  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return;

    // Never animate into a snapshot.
    if (isPrerender()) return;

    const reduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;

    // Hide the entrance targets before the browser paints.
    root.classList.add("crr-preanim");

    let cancelled = false;
    let ctx: { revert: () => void } | null = null;

    // If GSAP does not land, show everything anyway.
    const failsafe = window.setTimeout(() => {
      root.classList.remove("crr-preanim");
    }, 1600);

    Promise.all([import("gsap"), import("gsap/ScrollTrigger")])
      .then(([{ gsap }, { ScrollTrigger }]) => {
        if (cancelled) return;
        window.clearTimeout(failsafe);
        gsap.registerPlugin(ScrollTrigger);

        ctx = gsap.context(() => {
          const ease = "power2.out";

          // The class only existed to prevent the pre-paint flash. GSAP owns
          // the start state from here, via .from() below.
          root.classList.remove("crr-preanim");

          // --- Hero: one ordered entrance, top to bottom. ---
          const heroBits = gsap.utils.toArray<HTMLElement>(
            ".hero .trust, .hero h1, .hero .sub, .hero > .wrap > div > .btn, .hero .micro, .hero .chips-label",
          );
          const tl = gsap.timeline({ defaults: { ease, duration: 0.55 } });

          if (heroBits.length) {
            tl.from(heroBits, { y: 18, opacity: 0, stagger: 0.06 });
          }

          // Chips land as a group just behind the copy.
          const chips = gsap.utils.toArray<HTMLElement>(".hero .chip");
          if (chips.length) {
            tl.from(
              chips,
              { y: 12, opacity: 0, duration: 0.4, stagger: 0.04 },
              "-=0.25",
            );
          }

          // The sample reading is the proof; it arrives with the copy, not after.
          const reading = root.querySelector(".reading");
          if (reading) {
            tl.from(reading, { y: 24, opacity: 0, duration: 0.7 }, 0.15);
          }

          // --- Scroll reveals: one per section, fired once. ---
          gsap.utils
            .toArray<HTMLElement>("section.rule, section.crr-close")
            .forEach((section) => {
              // Most sections wrap their content in .wrap; the closing CTA
              // uses .crr-close__card instead. Reveal whichever it has.
              const holder =
                section.querySelector(":scope > .wrap") ??
                section.querySelector(":scope > .crr-close__card") ??
                section;
              const targets = gsap.utils.toArray<HTMLElement>(
                holder.children,
              );
              if (!targets.length) return;

              /* fromTo with immediateRender:false, NOT .from().
                 .from() applies its start state (opacity:0) the instant the
                 tween is created, and only clears it when the trigger fires.
                 If the trigger never fires, the content is invisible forever.
                 That is not hypothetical: .crr-root/.mrg-root set
                 overflow-x:hidden, which per spec forces overflow-y to auto
                 and makes the root a scroll container, so ScrollTrigger
                 (measuring the window) never fired and 24 elements across 8
                 sections sat at opacity 0 in a production build.
                 The overflow is fixed too, but this is the belt: with
                 immediateRender:false the start state is applied only when the
                 tween actually begins, so a trigger that never fires leaves
                 the content at its natural, VISIBLE state. */
              gsap.fromTo(
                targets,
                { y: 20, opacity: 0 },
                {
                  y: 0,
                  opacity: 1,
                  duration: 0.5,
                  ease,
                  stagger: 0.07,
                  immediateRender: false,
                  scrollTrigger: {
                    trigger: section,
                    start: "top 85%",
                    once: true,
                  },
                },
              );
            });

          ScrollTrigger.refresh();

          /* Last resort. If anything is still sitting at opacity 0 well after
             load, something about the scroll setup is wrong and an invisible
             page is far worse than an un-animated one. */
          window.setTimeout(() => {
            if (cancelled) return;
            root
              .querySelectorAll<HTMLElement>("section.rule *, section.crr-close *, section.mrg-close *")
              .forEach((el) => {
                if (el.style.opacity !== "" && parseFloat(el.style.opacity) < 0.99) {
                  el.style.opacity = "";
                  el.style.transform = "";
                }
              });
          }, 4000);
        }, root);
      })
      .catch(() => {
        // Import failed. The failsafe may not have fired yet, so clear here too.
        window.clearTimeout(failsafe);
        root.classList.remove("crr-preanim");
      });

    return () => {
      cancelled = true;
      window.clearTimeout(failsafe);
      root.classList.remove("crr-preanim");
      ctx?.revert();
    };
  }, [ref]);
}
